import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Settings,
  Mail,
  Building,
  Shield,
  Save,
  Plus,
  Edit2,
  Trash2,
  Lock,
  Info,
  Download,
} from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleColorMap = {
  SUPERADMIN: { bg: "bg-purple-500/10 border-purple-500/30", badge: "bg-purple-600 text-white" },
  GESTION: { bg: "bg-[#03BEE4]/10 border-[#03BEE4]/30", badge: "bg-[#03BEE4] text-white" },
  COMERCIAL: { bg: "bg-[#42E695]/10 border-[#42E695]/30", badge: "bg-[#42E695] text-[#04042C]" },
  TECNICO: { bg: "bg-orange-500/10 border-orange-500/30", badge: "bg-orange-500 text-white" },
  SOPORTE: { bg: "bg-cyan-500/10 border-cyan-500/30", badge: "bg-cyan-500 text-white" },
};

// Friendly labels for the 14 canonical modules. The module LIST itself and
// each module's applicable actions / exportable / scoped flags come from the
// backend `/api/rbac/meta` endpoint (single source of truth = rbac/registry.py),
// so this map is purely cosmetic and can never reintroduce orphan keys.
const MODULE_LABELS = {
  dashboard: "Dashboard",
  pipeline: "Pipeline de Ventas",
  customers: "Clientes",
  provisions: "Provisión",
  incidents: "Incidencias",
  requests: "Solicitudes",
  catalog: "Catálogo",
  providers: "Proveedores",
  users: "Usuarios",
  audit: "Auditoría",
  settings: "Configuración",
  checklist_master: "Checklist Maestro",
  it_tickets: "Tickets Internos",
  coverage_search: "Cobertura Fibra",
};

const actionLabels = { view: "Ver", create: "Crear", edit: "Editar", delete: "Eliminar", export: "Exportar" };

// Compact visual identity for each data-visibility scope level.
const SCOPE_META = {
  all:  { label: "Todos",   cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  team: { label: "Equipo",  cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  own:  { label: "Propios", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
};

// Mirrors registry.EXPORTABLE_MODULES — used only for the cosmetic role
// summary badges; the matrix modal uses the live `scoped` flag from the API.
const SCOPED_MODULES = new Set(["customers", "pipeline", "provisions", "incidents", "requests"]);

const ScopeBadge = ({ scope }) => {
  const m = SCOPE_META[scope] || SCOPE_META.own;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${m.cls}`}
      data-testid={`scope-badge-${scope}`}
    >
      {m.label}
    </span>
  );
};

// Read helpers that tolerate BOTH the legacy list format (["view"]) and the
// canonical v2 object ({actions:[...], scope:"team"}).
const actionsOf = (mperm) => (Array.isArray(mperm) ? mperm : (mperm?.actions || []));
const scopeOf = (mperm) => (mperm && !Array.isArray(mperm) && mperm.scope ? mperm.scope : "own");

// Permission matrix modal component — RBAC v2 (actions + export + scope).
// `modulesMeta` is the live list from /api/rbac/meta (canonical modules,
// applicable actions, exportable & scoped flags). Renders and saves the
// canonical v2 shape { module: { actions:[...], scope:"all|team|own" } }.
const PermissionMatrixModal = ({ role, open, onClose, onSave, isSystem, modulesMeta }) => {
  const [perms, setPerms] = useState({});

  useEffect(() => {
    if (role && modulesMeta.length) {
      // Build state ONLY from canonical modules → any orphan key stored on the
      // role (e.g. legacy `announcements`/`roles`) is dropped on next save.
      const copy = {};
      for (const meta of modulesMeta) {
        const mperm = role.permissions?.[meta.id];
        copy[meta.id] = { actions: [...actionsOf(mperm)], scope: scopeOf(mperm) };
      }
      setPerms(copy);
    }
  }, [role, modulesMeta]);

  const hasAction = (moduleId, action) => perms[moduleId]?.actions?.includes(action) ?? false;

  const toggleAction = (moduleId, action) => {
    const current = perms[moduleId]?.actions || [];
    let updated;
    if (current.includes(action)) {
      updated = current.filter((a) => a !== action);
      // Removing "view" removes everything (no action makes sense without it).
      if (action === "view") updated = [];
    } else {
      updated = [...current, action];
      // Any non-view action implies view.
      if (action !== "view" && !updated.includes("view")) updated.push("view");
    }
    setPerms({ ...perms, [moduleId]: { ...perms[moduleId], actions: updated } });
  };

  const setScope = (moduleId, scope) => {
    setPerms({ ...perms, [moduleId]: { ...perms[moduleId], scope } });
  };

  if (!role) return null;

  const colors = roleColorMap[role.id] || { badge: "bg-slate-600 text-white" };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="permission-matrix-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-3">
            Editar Permisos -
            <Badge className={colors.badge}>{role.name || role.id}</Badge>
            {isSystem && <Badge className="bg-[#1B1B4B] text-slate-300 border-none text-xs">Sistema</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {/* Info banners */}
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-300">
              <strong>Eliminar</strong> permite borrar registros de forma permanente. <strong>Exportar</strong> habilita la descarga de listados. El <strong>Alcance</strong> define qué datos ve el rol: <strong>Todos</strong>, solo los de su <strong>Equipo</strong> o solo los <strong>Propios</strong>. Los cambios afectan a todos los usuarios con este rol.
            </p>
          </div>

          {/* Matrix table */}
          <div className="rounded-lg border border-[#1E1E5F] overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-[#1B1B4B]/50">
                  <th className="text-left text-sm font-medium text-slate-300 p-3 w-[26%]">Módulo</th>
                  <th className="text-center text-sm font-medium text-slate-300 p-2">Ver</th>
                  <th className="text-center text-sm font-medium text-slate-300 p-2">Crear</th>
                  <th className="text-center text-sm font-medium text-slate-300 p-2">Editar</th>
                  <th className="text-center text-sm font-medium text-amber-400 p-2">
                    <span className="flex items-center justify-center gap-1"><Shield className="h-3 w-3" /> Eliminar</span>
                  </th>
                  <th className="text-center text-sm font-medium text-[#03BEE4] p-2">
                    <span className="flex items-center justify-center gap-1"><Download className="h-3 w-3" /> Exportar</span>
                  </th>
                  <th className="text-center text-sm font-medium text-slate-300 p-2 w-[18%]">Alcance</th>
                </tr>
              </thead>
              <tbody>
                {modulesMeta.map((mod, idx) => {
                  const rowScope = perms[mod.id]?.scope || "own";
                  return (
                    <tr key={mod.id} className={idx % 2 === 0 ? "bg-[#0A0A35]" : "bg-[#0F0F3D]"}>
                      <td className="p-3 text-sm text-white font-medium">{MODULE_LABELS[mod.id] || mod.id}</td>
                      {["view", "create", "edit"].map((action) => (
                        <td key={action} className="p-2 text-center">
                          {mod.actions.includes(action) ? (
                            <Checkbox
                              checked={hasAction(mod.id, action)}
                              onCheckedChange={() => toggleAction(mod.id, action)}
                              className="border-[#42E695] data-[state=checked]:bg-[#42E695] data-[state=checked]:text-[#04042C]"
                              data-testid={`perm-${mod.id}-${action}`}
                            />
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                      ))}
                      <td className="p-2 text-center">
                        {mod.actions.includes("delete") ? (
                          <Checkbox
                            checked={hasAction(mod.id, "delete")}
                            onCheckedChange={() => toggleAction(mod.id, "delete")}
                            className="border-amber-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-white"
                            data-testid={`perm-${mod.id}-delete`}
                          />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {mod.exportable ? (
                          <Checkbox
                            checked={hasAction(mod.id, "export")}
                            onCheckedChange={() => toggleAction(mod.id, "export")}
                            className="border-[#03BEE4] data-[state=checked]:bg-[#03BEE4] data-[state=checked]:text-[#04042C]"
                            data-testid={`perm-${mod.id}-export`}
                          />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {mod.scoped ? (
                          <div className="flex items-center justify-center gap-2">
                            <Select value={rowScope} onValueChange={(v) => setScope(mod.id, v)}>
                              <SelectTrigger
                                className="h-7 w-[104px] bg-[#0A0A35] border-[#1E1E5F] text-white text-xs"
                                data-testid={`scope-select-${mod.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                                <SelectItem value="all" data-testid={`scope-opt-${mod.id}-all`}>Todos</SelectItem>
                                <SelectItem value="team" data-testid={`scope-opt-${mod.id}-team`}>Equipo</SelectItem>
                                <SelectItem value="own" data-testid={`scope-opt-${mod.id}-own`}>Propios</SelectItem>
                              </SelectContent>
                            </Select>
                            <ScopeBadge scope={rowScope} />
                          </div>
                        ) : (
                          <span className="text-slate-600 text-xs">Global</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F] mt-4">
          <Button variant="outline" onClick={() => onClose(false)} className="border-[#1E1E5F] text-slate-300">
            Cancelar
          </Button>
          <Button
            onClick={() => onSave(perms)}
            className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
            data-testid="save-permissions-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            Guardar Cambios
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SettingsPage = () => {
  const { user } = useAuth();
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({
    global_notification_email: "administracion@wilan.es",
    company_name: "WiLAN",
    platform_name: "WiLAN",
    platform_subtitle: "Operations Center",
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemRoles, setSystemRoles] = useState([]);
  const [rbacMeta, setRbacMeta] = useState([]);
  const [editingSystemRole, setEditingSystemRole] = useState(null);
  const [editingCustomRole, setEditingCustomRole] = useState(null);
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", description: "", permissions: {} });
  const [roleToDelete, setRoleToDelete] = useState(null);

  const isSuperadmin = user?.roles?.includes("SUPERADMIN");

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, rolesRes, systemRolesRes, metaRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings`),
        axios.get(`${API_URL}/api/roles`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/system-roles`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/rbac/meta`).catch(() => ({ data: { modules: [] } })),
      ]);
      setSettings({
        ...settingsRes.data,
        platform_name: settingsRes.data.platform_name || "WiLAN",
        platform_subtitle: settingsRes.data.platform_subtitle || "Operations Center",
      });
      setRoles(rolesRes.data);
      setSystemRoles(systemRolesRes.data.filter(r => r.id !== "IT"));
      setRbacMeta(metaRes.data.modules || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings);
      toast.success("Configuración guardada");
      refreshSettings();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name.trim()) { toast.error("El nombre del rol es obligatorio"); return; }
    try {
      await axios.post(`${API_URL}/api/roles`, newRole);
      toast.success("Rol creado correctamente");
      setCreateRoleOpen(false);
      setNewRole({ name: "", description: "", permissions: {} });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear rol");
    }
  };

  const handleSaveSystemRolePermissions = async (permissions) => {
    if (!editingSystemRole) return;
    try {
      await axios.put(`${API_URL}/api/system-roles/${editingSystemRole.id}`, { permissions });
      toast.success(`Permisos de ${editingSystemRole.name} actualizados`);
      setEditingSystemRole(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar permisos");
    }
  };

  const handleSaveCustomRolePermissions = async (permissions) => {
    if (!editingCustomRole) return;
    try {
      await axios.put(`${API_URL}/api/roles/${editingCustomRole.id}`, { permissions });
      toast.success(`Rol "${editingCustomRole.name}" actualizado`);
      setEditingCustomRole(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar rol");
    }
  };

  const handleDeleteRole = async () => {
    // Uses an AlertDialog-driven flow instead of window.confirm because the
    // preview iframe (and several browsers) suppress native confirm dialogs,
    // which caused the delete click to appear silently broken.
    if (!roleToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/roles/${roleToDelete.id}`);
      toast.success("Rol eliminado");
      setRoleToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar rol");
    }
  };

  const summarizePermissions = (permissions) => {
    if (!permissions) return [];
    return Object.entries(permissions)
      .map(([moduleId, mperm]) => {
        const acts = actionsOf(mperm);
        const label = MODULE_LABELS[moduleId];
        if (!label || !acts || acts.length === 0) return null;
        const scopeSuffix = SCOPED_MODULES.has(moduleId)
          ? ` · ${SCOPE_META[scopeOf(mperm)]?.label || scopeOf(mperm)}`
          : "";
        return `${label}: ${acts.map(a => actionLabels[a] || a).join(", ")}${scopeSuffix}`;
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
          <Settings className="h-8 w-8 text-[#03BEE4]" />
          Configuración del Sistema
        </h1>
        <p className="text-slate-400 mt-1">Ajustes globales y personalización</p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-[#0A0A35] border border-[#1E1E5F] p-1 flex-wrap">
          <TabsTrigger value="general" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
            General
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
            Marca / Branding
          </TabsTrigger>
          {isSuperadmin && (
            <TabsTrigger value="roles" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]" data-testid="roles-tab-trigger">
              Roles y Permisos
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card className="bg-[#0A0A35] border-[#1E1E5F]">
            <CardHeader>
              <CardTitle className="text-white font-heading flex items-center gap-2">
                <Settings className="h-5 w-5 text-[#03BEE4]" />
                Configuración General
              </CardTitle>
              <CardDescription className="text-slate-400">Ajustes globales del sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Building className="h-4 w-4" /> Nombre de la Empresa
                  </Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email de Notificaciones Global
                  </Label>
                  <Input
                    type="email"
                    value={settings.global_notification_email}
                    onChange={(e) => setSettings({ ...settings, global_notification_email: e.target.value })}
                    data-testid="global-email-input"
                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Este email recibirá todas las notificaciones del sistema</p>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t border-[#1E1E5F]">
                <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]">
                  {saving ? "Guardando..." : <><Save className="h-4 w-4 mr-2" /> Guardar Configuración</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Settings */}
        <TabsContent value="branding">
          <Card className="bg-[#0A0A35] border-[#1E1E5F]">
            <CardHeader>
              <CardTitle className="text-white font-heading flex items-center gap-2">
                <Building className="h-5 w-5 text-[#03BEE4]" />
                Personalización de Marca
              </CardTitle>
              <CardDescription className="text-slate-400">Personaliza el nombre e imagen de la plataforma</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300">Nombre de la Plataforma</Label>
                  <Input
                    value={settings.platform_name || ""}
                    onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
                    placeholder="WiLAN"
                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">Se mostrará en el sidebar y la página de login</p>
                </div>
                <div>
                  <Label className="text-slate-300">Subtítulo</Label>
                  <Input
                    value={settings.platform_subtitle || ""}
                    onChange={(e) => setSettings({ ...settings, platform_subtitle: e.target.value })}
                    placeholder="Operations Center"
                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  />
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                <p className="text-slate-400 text-sm">
                  <strong className="text-[#03BEE4]">Nota:</strong> Para cambiar el logo, contacta con el equipo de desarrollo o sube tu imagen en la sección de archivos.
                </p>
              </div>
              <div className="flex justify-end pt-4 border-t border-[#1E1E5F]">
                <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]">
                  <Save className="h-4 w-4 mr-2" /> Guardar Cambios
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles & Permissions Tab - SUPERADMIN only */}
        {isSuperadmin && (
        <TabsContent value="roles">
          <Card className="bg-[#0A0A35] border-[#1E1E5F]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white font-heading flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#03BEE4]" />
                  Gestión de Roles y Permisos
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configura qué puede hacer cada rol en cada área del sistema
                </CardDescription>
              </div>
              <Dialog open={createRoleOpen} onOpenChange={setCreateRoleOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]" data-testid="create-role-btn">
                    <Plus className="h-4 w-4 mr-2" /> Nuevo Rol
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-white font-heading">Crear Rol Personalizado</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-slate-300">Nombre del Rol *</Label>
                      <Input
                        value={newRole.name}
                        onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                        placeholder="Ej: Comercial Senior"
                        className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                        data-testid="new-role-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Descripción</Label>
                      <Input
                        value={newRole.description}
                        onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                        placeholder="Ej: Acceso ampliado a ventas"
                        className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                        data-testid="new-role-desc-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Los permisos se pueden configurar después de crear el rol.
                    </p>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button variant="outline" onClick={() => setCreateRoleOpen(false)} className="border-[#1E1E5F] text-slate-300">
                        Cancelar
                      </Button>
                      <Button onClick={handleCreateRole} className="bg-[#42E695] text-[#04042C]" data-testid="confirm-create-role-btn">
                        Crear Rol
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>

            <CardContent className="space-y-8">
              {/* System Roles Section */}
              <div>
                <h3 className="text-lg font-heading font-semibold text-white mb-4 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-[#03BEE4]" />
                  Roles del Sistema
                  <Badge className="bg-[#1B1B4B] text-slate-300 border-none ml-2 text-xs">No se pueden eliminar ni renombrar</Badge>
                </h3>
                <div className="space-y-3">
                  {systemRoles.map((role) => {
                    const colors = roleColorMap[role.id] || { bg: "bg-slate-500/10 border-slate-500/30", badge: "bg-slate-600 text-white" };
                    const permSummary = summarizePermissions(role.permissions);
                    return (
                      <div key={role.id} className={`p-4 rounded-lg border ${colors.bg}`} data-testid={`system-role-${role.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Badge className={colors.badge}>{role.name || role.id}</Badge>
                            <Badge className="bg-[#1B1B4B] text-slate-400 border-none text-xs">Sistema</Badge>
                            {role.description && <span className="text-slate-400 text-sm hidden sm:inline">{role.description}</span>}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingSystemRole(role)}
                            className="border-[#1E1E5F] text-[#03BEE4] hover:bg-[#1B1B4B]"
                            data-testid={`edit-system-role-${role.id}`}
                          >
                            <Edit2 className="h-4 w-4 mr-1" /> Editar Permisos
                          </Button>
                        </div>
                        {permSummary.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {permSummary.map((s, i) => (
                              <Badge key={i} className="bg-[#1E1E5F]/50 text-slate-300 border-none text-xs">{s}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Roles Section */}
              <div>
                <h3 className="text-lg font-heading font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#42E695]" />
                  Roles Personalizados
                  <Badge className="bg-[#1B1B4B] text-slate-300 border-none ml-2">{roles.length}</Badge>
                </h3>
                {roles.length === 0 ? (
                  <div className="p-8 text-center border border-dashed border-[#1E1E5F] rounded-lg">
                    <Shield className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400 mb-2">No hay roles personalizados</p>
                    <p className="text-slate-500 text-sm">Crea roles como "Solo Lectura", "Comercial Junior", etc.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {roles.map((role) => {
                      const permSummary = summarizePermissions(role.permissions);
                      return (
                        <div key={role.id} className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]" data-testid={`custom-role-${role.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="text-white font-medium">{role.name}</h4>
                              {role.description && <p className="text-slate-400 text-sm">{role.description}</p>}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingCustomRole(role)}
                                className="border-[#1E1E5F] text-[#03BEE4] hover:bg-[#1B1B4B]"
                                data-testid={`edit-custom-role-${role.id}`}
                              >
                                <Edit2 className="h-4 w-4 mr-1" /> Permisos
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setRoleToDelete(role)}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`delete-custom-role-${role.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {permSummary.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {permSummary.map((s, i) => (
                                <Badge key={i} className="bg-[#1E1E5F] text-slate-300 border-none text-xs">{s}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit System Role Modal */}
          <PermissionMatrixModal
            role={editingSystemRole}
            open={!!editingSystemRole}
            onClose={() => setEditingSystemRole(null)}
            onSave={handleSaveSystemRolePermissions}
            isSystem
            modulesMeta={rbacMeta}
          />

          {/* Edit Custom Role Modal */}
          <PermissionMatrixModal
            role={editingCustomRole}
            open={!!editingCustomRole}
            onClose={() => setEditingCustomRole(null)}
            onSave={handleSaveCustomRolePermissions}
            isSystem={false}
            modulesMeta={rbacMeta}
          />

          {/* Delete Custom Role Confirmation */}
          <AlertDialog open={!!roleToDelete} onOpenChange={(o) => !o && setRoleToDelete(null)}>
            <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]" data-testid="delete-role-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">¿Eliminar rol personalizado?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  Esta acción eliminará permanentemente el rol{" "}
                  <span className="text-[#42E695] font-semibold">{roleToDelete?.name}</span>.
                  Si hay usuarios asignados a este rol, la operación será rechazada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteRole}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-role-btn"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
